import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Users,
  Search,
  UserPlus,
  TrendingUp,
  TrendingDown,
  Target,
  FileText,
  StickyNote,
  Dumbbell,
  ChevronRight,
  Mail,
  Phone,
  Calendar,
  Scale,
  Activity,
  MoreVertical,
  Eye,
  Edit,
  Loader2
} from 'lucide-react';

export default function ClientManagementPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [total, setTotal] = useState(0);

  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/clients/list`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { search: searchTerm || undefined, limit: 50 }
      });
      setClients(response.data.clients);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching clients:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to view clients');
      }
    } finally {
      setLoading(false);
    }
  }, [token, searchTerm]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchClients();
  };

  const getGoalColor = (goal) => {
    const colors = {
      fat_loss: 'text-orange-400 bg-orange-500/10',
      muscle_gain: 'text-blue-400 bg-blue-500/10',
      strength: 'text-yellow-400 bg-yellow-500/10',
      endurance: 'text-green-400 bg-green-500/10',
      general_fitness: 'text-purple-400 bg-purple-500/10',
      sport_specific: 'text-pink-400 bg-pink-500/10'
    };
    return colors[goal] || 'text-gray-400 bg-gray-500/10';
  };

  const formatGoal = (goal) => {
    return goal?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Not Set';
  };

  if (user?.role === 'client') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Users className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <h2 className="text-xl text-white mb-2">Access Restricted</h2>
          <p className="text-gray-400">This page is only available for trainers and admins.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="client-management-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase">
            CLIENT <span className="text-primary">MANAGEMENT</span>
          </h1>
          <p className="text-gray-400 mt-1">{total} total clients</p>
        </div>
        <Button 
          onClick={() => navigate('/clients/intake/new')}
          className="bg-primary hover:bg-red-700"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add New Client
        </Button>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            type="text"
            placeholder="Search clients by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-secondary border-border"
          />
        </div>
        <Button type="submit" variant="outline">
          Search
        </Button>
      </form>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-tactical text-center">
          <Users className="w-6 h-6 text-blue-500 mx-auto mb-2" />
          <div className="font-mono text-2xl text-white">{total}</div>
          <div className="text-xs text-gray-400">Total Clients</div>
        </div>
        <div className="card-tactical text-center">
          <FileText className="w-6 h-6 text-green-500 mx-auto mb-2" />
          <div className="font-mono text-2xl text-white">
            {clients.filter(c => c.intake_completed).length}
          </div>
          <div className="text-xs text-gray-400">Intake Complete</div>
        </div>
        <div className="card-tactical text-center">
          <Dumbbell className="w-6 h-6 text-orange-500 mx-auto mb-2" />
          <div className="font-mono text-2xl text-white">
            {clients.filter(c => c.active_programs > 0).length}
          </div>
          <div className="text-xs text-gray-400">Active Programs</div>
        </div>
        <div className="card-tactical text-center">
          <Scale className="w-6 h-6 text-purple-500 mx-auto mb-2" />
          <div className="font-mono text-2xl text-white">
            {clients.filter(c => c.latest_body_composition).length}
          </div>
          <div className="text-xs text-gray-400">Body Comp Tracked</div>
        </div>
      </div>

      {/* Client List */}
      <div className="space-y-3">
        {clients.length === 0 ? (
          <div className="card-tactical text-center py-12">
            <Users className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <h3 className="text-white font-semibold mb-2">No Clients Found</h3>
            <p className="text-gray-400 text-sm mb-4">
              {searchTerm ? 'Try a different search term' : 'Start by adding your first client'}
            </p>
            <Button onClick={() => navigate('/clients/intake/new')} className="bg-primary">
              <UserPlus className="w-4 h-4 mr-2" />
              Add Client
            </Button>
          </div>
        ) : (
          clients.map((client) => (
            <div
              key={client.id}
              data-testid={`client-card-${client.id}`}
              className="card-tactical hover:border-primary/30 transition-colors cursor-pointer"
              onClick={() => navigate(`/clients/${client.id}`)}
            >
              <div className="flex items-center gap-4">
                {/* Avatar */}
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-lg">
                  {client.first_name?.[0]?.toUpperCase() || client.email[0].toUpperCase()}
                  {client.last_name?.[0]?.toUpperCase() || ''}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-white font-semibold truncate">
                      {client.first_name && client.last_name 
                        ? `${client.first_name} ${client.last_name}`
                        : client.email}
                    </h3>
                    {client.primary_goal && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getGoalColor(client.primary_goal)}`}>
                        {formatGoal(client.primary_goal)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-400 mt-1">
                    <span className="flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {client.email}
                    </span>
                    {client.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {client.phone}
                      </span>
                    )}
                  </div>
                </div>

                {/* Status Badges */}
                <div className="hidden md:flex items-center gap-3">
                  {/* Intake Status */}
                  <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded ${
                    client.intake_completed ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'
                  }`}>
                    <FileText className="w-3 h-3" />
                    {client.intake_completed ? 'Intake Done' : 'Pending'}
                  </div>

                  {/* Body Comp */}
                  {client.latest_body_composition && (
                    <div className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-purple-500/10 text-purple-400">
                      <Scale className="w-3 h-3" />
                      {client.latest_body_composition.weight_lbs} lbs
                    </div>
                  )}

                  {/* Programs */}
                  {client.active_programs > 0 && (
                    <div className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-blue-500/10 text-blue-400">
                      <Dumbbell className="w-3 h-3" />
                      {client.active_programs} program{client.active_programs > 1 ? 's' : ''}
                    </div>
                  )}

                  {/* Notes */}
                  {client.note_count > 0 && (
                    <div className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-gray-500/10 text-gray-400">
                      <StickyNote className="w-3 h-3" />
                      {client.note_count}
                    </div>
                  )}
                </div>

                {/* Arrow */}
                <ChevronRight className="w-5 h-5 text-gray-500" />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
